import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { Round } from "../types";

interface PlayerPairingsOverviewProps {
  rounds: Round[];
  playerNumbers?: Record<string, number>;
  getPlayerName: (playerId: string) => string;
}

interface RoundPairing {
  partnerId: string;
  opponentIds: string[];
  courtNumber: number;
}

// Shows, for every player, who they partner and who they face in each round,
// highlighting anyone they share a court with more than once during the day.
function PlayerPairingsOverview({
  rounds,
  playerNumbers = {},
  getPlayerName,
}: PlayerPairingsOverviewProps) {
  const pairingsByPlayer: Record<string, Record<number, RoundPairing>> = {};
  const meetings: Record<string, Record<string, number>> = {};

  const addMeeting = (a: string, b: string) => {
    meetings[a] = meetings[a] || {};
    meetings[a][b] = (meetings[a][b] || 0) + 1;
  };

  rounds.forEach((round) => {
    round.matches.forEach((match) => {
      const pair1 = [match.pair1.player1Id, match.pair1.player2Id];
      const pair2 = [match.pair2.player1Id, match.pair2.player2Id];

      [
        { team: pair1, other: pair2 },
        { team: pair2, other: pair1 },
      ].forEach(({ team, other }) => {
        team.forEach((playerId, index) => {
          const partnerId = team[1 - index];
          pairingsByPlayer[playerId] = pairingsByPlayer[playerId] || {};
          pairingsByPlayer[playerId][round.roundNumber] = {
            partnerId,
            opponentIds: other,
            courtNumber: match.courtNumber,
          };
          addMeeting(playerId, partnerId);
          other.forEach((opponentId) => addMeeting(playerId, opponentId));
        });
      });
    });
  });

  const playerIds = Object.keys(pairingsByPlayer).sort((a, b) => {
    const numA = playerNumbers[a];
    const numB = playerNumbers[b];
    if (numA !== undefined && numB !== undefined) return numA - numB;
    if (numA !== undefined) return -1;
    if (numB !== undefined) return 1;
    return getPlayerName(a).localeCompare(getPlayerName(b));
  });

  if (playerIds.length === 0) return null;

  const renderName = (viewerId: string, otherId: string) => {
    const repeated = (meetings[viewerId]?.[otherId] || 0) > 1;
    return (
      <Box
        component="span"
        sx={repeated ? { color: "warning.main", fontWeight: 700 } : undefined}
      >
        {getPlayerName(otherId)}
      </Box>
    );
  };

  return (
    <Accordion sx={{ mb: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Partners & Opponents</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 0, sm: 2 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", px: { xs: 2, sm: 0 }, mb: 1 }}
        >
          🤝 plays with · ⚔️ plays against. Names in orange share a court with
          that player more than once today.
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    fontWeight: 700,
                  }}
                >
                  Player
                </TableCell>
                {rounds.map((round) => (
                  <TableCell key={round.roundNumber} sx={{ fontWeight: 700 }}>
                    Round {round.roundNumber}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Unique Players Met
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {playerIds.map((playerId) => (
                <TableRow key={playerId} hover>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {playerNumbers[playerId] !== undefined
                      ? `P${playerNumbers[playerId]} · `
                      : ""}
                    {getPlayerName(playerId)}
                  </TableCell>
                  {rounds.map((round) => {
                    const pairing = pairingsByPlayer[playerId]?.[round.roundNumber];
                    return (
                      <TableCell key={round.roundNumber} sx={{ verticalAlign: "top" }}>
                        {pairing ? (
                          <>
                            <Typography variant="body2">
                              🤝 {renderName(playerId, pairing.partnerId)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ⚔️{" "}
                              {pairing.opponentIds.map((opponentId, index) => (
                                <React.Fragment key={opponentId}>
                                  {index > 0 && " & "}
                                  {renderName(playerId, opponentId)}
                                </React.Fragment>
                              ))}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              Court {pairing.courtNumber}
                            </Typography>
                          </>
                        ) : (
                          "–"
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {Object.keys(meetings[playerId] || {}).length}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

export default PlayerPairingsOverview;
